/* eslint-disable no-useless-escape */
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getContrast } from 'polished';
import { useTenantConfigurations } from '../hooks/useTenantConfigurations';
import { MONTHS } from '../constants';

const CSS_VAR_PATTERN = /var\(\s*(--[^,\s)]+)\s*(?:,\s*([^)]+?)\s*)?\)/;

export const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function formatDate(date: string): string {
  if (!date) return '';

  try {
    return format(parseISO(date), 'dd MMM yy', {
      locale: ptBR,
    });
  } catch (error) {
    return date;
  }
}

export function formatPercent(value: number) {
  return numberRound(value).toFixed(2).replace('.', ',') + '%';
}

type GetContrastTextColorOptions = {
  lightColor?: string;
  darkColor?: string;
};

export function isRobotoFontFamily(fontFamily?: string | null) {
  if (!fontFamily) return true;

  return fontFamily.toLowerCase().includes('roboto');
}

function resolveCssColor(color: string) {
  if (typeof document === 'undefined') return color;

  const match = color.match(CSS_VAR_PATTERN);
  if (!match) return color;

  const [, variableName, fallback] = match;
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();

  return resolved || fallback?.trim() || color;
}

export function getContrastTextColor(
  backgroundColor: string,
  options: GetContrastTextColorOptions = {},
) {
  const { lightColor = '#FFFFFF', darkColor = '#333F50' } = options;

  try {
    const resolvedBackgroundColor = resolveCssColor(backgroundColor);
    const resolvedLightColor = resolveCssColor(lightColor);
    const resolvedDarkColor = resolveCssColor(darkColor);

    const lightContrast = getContrast(
      resolvedBackgroundColor,
      resolvedLightColor,
    );
    const darkContrast = getContrast(
      resolvedBackgroundColor,
      resolvedDarkColor,
    );

    return lightContrast >= darkContrast ? lightColor : darkColor;
  } catch (error) {
    return darkColor;
  }
}

export function getMonthYearFromDateISO(date: string, lang = 'pt-BR'): string {
  if (!date) return '';

  try {
    const parts = date.slice(0, -1).split('T');
    const dateComponent = parts[0];
    if (!dateComponent) {
      return date;
    }

    return format(parseISO(dateComponent), 'MMMM/yyyy', {
      locale: ptBR,
    });
  } catch (error) {
    return date;
  }
}

export function formatDateFull(date: string): string {
  if (!date) return '';

  try {
    return format(parseISO(date), 'dd/MM/yyyy', {
      locale: ptBR,
    });
  } catch (error) {
    return date;
  }
}

export function formateISODate(dateISO: string, form: string) {
  if (!dateISO) return '';

  try {
    const parts = dateISO.slice(0, -1).split('T');
    const dateComponent = parts[0];
    if (!dateComponent) {
      return dateISO;
    }

    return format(parseISO(dateComponent), form, {
      locale: ptBR,
    });
  } catch (error) {
    return dateISO;
  }
}

export function fromPtMonthToMiniMonth(ptMonth: string) {
  const monthFound = MONTHS.PT.find((m) => m.name === ptMonth);

  if (!monthFound) {
    return ptMonth;
  }

  const monthNumber = monthFound.month_number_string;

  const now = new Date();

  const date = new Date(`${now.getFullYear()}-${monthNumber}-01 00:00:00`);

  console.log('ptMonth', ptMonth);
  console.log('monthNumber', monthNumber);
  console.log('date', date);

  const formattedDate = format(date, 'MMM', {
    locale: ptBR,
  });

  return formattedDate;
}

export function formatDateFullWithHour(date: string): string {
  if (!date) return '';

  try {
    return format(parseISO(date), 'dd/MM/yyyy HH:mm:ss', {
      locale: ptBR,
    });
  } catch (error) {
    return date;
  }
}

export function formatDateFullInString(date: string): string {
  if (!date || typeof date !== 'string') return '';

  try {
    const [dateComponent] = date.split('T');
    if (!dateComponent) {
      return '';
    }
    const parsedDate = parseISO(dateComponent);
    if (!isValid(parsedDate)) {
      return '';
    }
    return format(parsedDate, 'dd/MM/yyyy', {
      locale: ptBR,
    });
  } catch (error) {
    return '';
  }
}

export function formatDateHour(date: string): string {
  if (!date) return '';

  try {
    return format(parseISO(date), 'dd/MM/yyyy ', {
      locale: ptBR,
    });
  } catch (error) {
    return date;
  }
}

// export function formatBrMoney(
//   value: number,
//   removeDecimals: boolean = false,
//   is_using_br_money: boolean = true,
// ): string {
//   return new Intl.NumberFormat(is_using_br_money ? 'pt-BR' : 'en-US', {
//     style: 'currency',
//     currency: is_using_br_money ? 'BRL' : 'USD',
//     maximumFractionDigits: removeDecimals ? 0 : 2,
//     minimumFractionDigits: 0,
//   }).format(Number(value));
// }

export function formatMoney({
  value,
  sign = 'R$',
  decimals = 2,
  thousandSize = 3,
  thousandSeparator = '.',
  deciamlSeparator = ',',
  isUsingBrMoney = true,
}: {
  value: number;
  sign?: string;
  decimals?: number;
  thousandSize?: number;
  thousandSeparator?: string;
  deciamlSeparator?: string;
  isUsingBrMoney?: boolean;
}) {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return '';
  }

  const re =
      '\\d(?=(\\d{' +
      (thousandSize || 3) +
      '})+' +
      (decimals > 0 ? '\\D' : '$') +
      ')',
    num = numericValue.toFixed(Math.max(0, ~~decimals));

  const result = (
    deciamlSeparator ? num.replace('.', deciamlSeparator) : num
  ).replace(new RegExp(re, 'g'), '$&' + (thousandSeparator || ','));

  if (!isUsingBrMoney) {
    return `$ ${result}`;
  }

  return `${sign} ${result}`;
}

type CalculateTextColumnSizeParams<T> = {
  rows?: T[] | null;
  getValue: (row: T) => string | number | null | undefined;
  minSize: number;
  fallbackText?: string;
  charWidth?: number;
  maxSize?: number;
};

export function calculateTextColumnSize<T>({
  rows,
  getValue,
  minSize,
  fallbackText = '',
  charWidth = 15,
  maxSize,
}: CalculateTextColumnSizeParams<T>) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const maxTextLength = safeRows.reduce((max, row) => {
    const value = String(getValue(row) ?? '').trim();
    return Math.max(max, value.length);
  }, String(fallbackText).trim().length);

  const calculatedSize = Math.max(minSize, maxTextLength * charWidth);

  return typeof maxSize === 'number'
    ? Math.min(calculatedSize, maxSize)
    : calculatedSize;
}

type SetGridParams = {
  point: number;
  colSpan: number;
  inMinScreen?: boolean;
};

export function setGrid({
  point,
  colSpan,
  inMinScreen = false,
}: SetGridParams) {
  return {
    [`@media screen and (${inMinScreen ? 'min' : 'max'}-width: ${point}px)`]: {
      gridColumn: `span ${colSpan}`,
    },
  };
}

// The `content-type` header read from an AxiosResponse is typed as
// `AxiosHeaderValue | undefined` (axios 1.x). Accept that shape here and coerce
// it to a string for the Blob constructor instead of casting at ~20 call sites.
type DownloadFileFromServerParams = {
  contentType: import('axios').AxiosHeaderValue | undefined;
  filename: string;
  data: any;
};

// export const generateRandString = () => crypto.randomBytes(20).toString('hex');
function makeid(length: any) {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const generateRandString = () => makeid(30);

export function downloadFileFromServer({
  contentType,
  data,
  filename,
}: DownloadFileFromServerParams) {
  const blob = new Blob([data], {
    type: typeof contentType === 'string' ? contentType : undefined,
  });
  const fileURL = URL.createObjectURL(blob);

  const link = document.createElement('a');

  link.href = fileURL;
  link.setAttribute('download', removeDoubleQuotes(filename));

  document.body.appendChild(link);

  link.click();
  link.parentNode?.removeChild(link);
}

export function numberRound(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function removeDoubleQuotes(s: string) {
  return s.replace(/^"(.*)"$/, '$1');
}

export function uniqueValues(arr: any[], props: string[]) {
  return arr.filter(
    (v, i, a) =>
      a.findIndex((v2) => [...props].every((k) => v2[k] === v[k])) === i,
  );
}

export function uniqueValuesList<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

export function firstLetterUpper(value: string): string {
  if (value) {
    const word = value.substring(1);

    return `${value[0].toUpperCase()}${word}`;
  } else {
    return '';
  }
}

export function splitText(text: string, number: number) {
  if (text?.length > number) {
    return text.substring(0, number) + '...';
  }
  return text;
}

export function refreshPage() {
  window.location.reload();
}

export function fixedDecimal(value: number, dec = 2) {
  return Number(value.toFixed(dec));
}

export function unmaskValue(value: string, parse = true) {
  const unmasked = value
    .replace(/\./g, '')
    .replace(/,/g, '.')
    .replace(/\s/g, '')
    .replace(/%/g, '')
    .replace(/R\$/g, '')
    .trim();
  return parse ? Number(unmasked) : unmasked;
}

export function removeDuplicateValues<T, P extends keyof T>(
  items: T[],
  propNames: P[],
) {
  const propNamesArray = Array.from(propNames);
  const isPropValuesEqual = (subject: any, target: any, propNames: P[]) =>
    propNames.every((propName) => subject[propName] === target[propName]);

  return items.filter(
    (item, index, array) =>
      index ===
      array.findIndex((foundItem) =>
        isPropValuesEqual(foundItem, item, propNamesArray),
      ),
  );
}

// export function removeDuplicateValues<T, P extends keyof T>(data: T[], key: P) {
//   const uniqueArray = data.filter((value, index) => {
//     const _value = JSON.stringify(value);
//     return (
//       index ===
//       data.findIndex((obj) => {
//         return JSON.stringify(obj) === _value;
//       })
//     );
//   });

//   return uniqueArray;
// }

export function sortObjectsByNumberField<T>(
  objArray: T[],
  keyNumber: string,
  desc = true,
): T[] {
  let sorted: T[] = [];

  if (desc) {
    sorted = objArray.sort((a: any, b: any) => {
      return b[keyNumber] - a[keyNumber];
    });
  } else {
    sorted = objArray.sort((a: any, b: any) => {
      return a[keyNumber] - b[keyNumber];
    });
  }

  return sorted;
}

export function sortByNumber<T>(objArray: T[], desc = true): T[] {
  let sorted: T[] = [];

  if (desc) {
    sorted = objArray.sort((a: any, b: any) => {
      return Number(b) - Number(a);
    });
  } else {
    sorted = objArray.sort((a: any, b: any) => {
      return Number(a) - Number(b);
    });
  }

  return sorted;
}

export function countRemainingMonthsYear() {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const remainingMonths = 11 - currentMonth;
  return remainingMonths;
}

export function truncateText(text: string | undefined | null, maxLength = 20) {
  if (!text) {
    return '';
  }

  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

export function sumFieldInArrayOfObject<T, P extends keyof T>(
  array: T[],
  field: P,
  round = false,
): number {
  const sum = array.reduce(
    (partialSum, a) => <any>partialSum + <any>a[field],
    0,
  );
  if (round) {
    return numberRound(sum);
  }
  return sum;
}

export function incrementPercentOnValue(value: number, percent: number) {
  const percentCalc = percent / 100;
  const valueIncrease = value * percentCalc;
  return numberRound(value + valueIncrease);
}

export function multiplyPercentOnValue(value: number, percent: number) {
  const percentCalc = percent / 100;
  const valueIncrease = value * percentCalc;
  return numberRound(valueIncrease);
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function toSlug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

export function objectHasAnyProperty(obj: object): boolean {
  if (!obj) {
    return false;
  }

  return Object.keys(obj).length > 0;
}

export const formatCompactNumber = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const sign = value < 0 ? '-' : '';
  const absValue = Math.abs(value);

  const formatWithTrim = (num: number, decimals: number) => {
    const fixed = num.toFixed(decimals);
    if (!fixed.includes('.')) return fixed;
    return fixed.replace(/\.?0+$/, '');
  };

  if (absValue >= 1_000_000_000) {
    return `${sign}${formatWithTrim(absValue / 1_000_000_000, 2)}B`;
  } else if (absValue >= 1_000_000) {
    return `${sign}${formatWithTrim(absValue / 1_000_000, 2)}M`;
  } else if (absValue >= 1_000) {
    const thousands = absValue / 1_000;
    const decimalPlaces = thousands < 100 ? 1 : 0;
    const formattedThousands = formatWithTrim(thousands, decimalPlaces);
    const numericThousands = parseFloat(formattedThousands);

    if (numericThousands >= 1000) {
      return `${sign}${formatWithTrim(absValue / 1_000_000, 2)}M`;
    }

    return `${sign}${formattedThousands}k`;
  }

  return `${sign}${absValue}`;
};
